import { ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useCartStore } from '@/features/Cart/stores/cart';
import { matchesAnyBaseSku } from '@/composables/productHelper';
import { useRandomisation } from '@/features/Product/composables/useRandomisation';
import { useMysteryProduct } from '@/features/Product/composables/useMysteryProduct';
import { getProductBySku } from '@/features/Product/composables/productLookupHelper';
import {
  findVariantParentMaxQuantityFamilies,
  getFamilyTotalQuantity,
} from './variantParentMaxQuantity';
import type { CartRule } from '@/features/Cart/types/models';
import type { AddToCartItem, ShopifyCartItem } from '@/features/Cart/types/servicesShopifyCart';
import type {
  BundleDetails,
  CheckoutProductSelection,
  RandomisationDetails,
  RandomisationException,
} from '@/features/Product/types/models';

const getCappedFamilies = (baselineItems: ShopifyCartItem[]) =>
  findVariantParentMaxQuantityFamilies().filter(
    (family) => getFamilyTotalQuantity(baselineItems, family) >= family.maxQuantity,
  );

const getExceededFamilies = (items: ShopifyCartItem[]) =>
  findVariantParentMaxQuantityFamilies().filter(
    (family) => getFamilyTotalQuantity(items, family) > family.maxQuantity,
  );

const excludeCappedFamilySkus = (
  randomisationDetails: RandomisationDetails | undefined,
  baselineItems: ShopifyCartItem[],
): RandomisationDetails | undefined => {
  if (!randomisationDetails?.options?.length) return randomisationDetails;
  const cappedFamilies = getCappedFamilies(baselineItems);
  if (!cappedFamilies.length) return randomisationDetails;
  return {
    ...randomisationDetails,
    options: randomisationDetails.options.filter(
      (option) =>
        !cappedFamilies.some((family) => matchesAnyBaseSku(option.sku, family.memberSkus)),
    ),
  };
};

const giftItemNeedsReroll = (item: ShopifyCartItem, cartItems: ShopifyCartItem[]): boolean => {
  const customBundleDetails = item.properties?._custom_bundle_details;
  if (!customBundleDetails) return false;
  const overCapacityFamilies = getExceededFamilies(cartItems);
  if (!overCapacityFamilies.length) return false;
  try {
    const skus: string[] = JSON.parse(customBundleDetails).map(
      (detail: { sku: string }) => detail.sku,
    );
    return skus.some((sku) =>
      overCapacityFamilies.some((family) => matchesAnyBaseSku(sku, family.memberSkus)),
    );
  } catch {
    return false;
  }
};

const giftItemExceptionMismatch = (
  item: ShopifyCartItem,
  exception: RandomisationException | undefined,
  exceptionApplies: boolean,
): boolean => {
  if (!exception) return false;
  const customBundleDetails = item.properties?._custom_bundle_details;
  if (!customBundleDetails) return false;
  try {
    const skus: string[] = JSON.parse(customBundleDetails).map(
      (detail: { sku: string }) => detail.sku,
    );
    return skus.includes(exception.assignedSku.sku) !== exceptionApplies;
  } catch {
    return false;
  }
};

const buildFixedBundleCustomDetails = (
  bundleDefinition: BundleDetails,
): CheckoutProductSelection[] =>
  bundleDefinition.bundleItems
    .filter((item) => item.itemType === 'fixed')
    .flatMap((item) =>
      (item.processedProducts || []).map((product) => ({
        id: `gid://shopify/ProductVariant/${product.attributes.variantId}` as const,
        sku: product.attributes.sku,
        quantity: item.quantity || 1,
      })),
    );

export const cartRuleFreeGift: CartRule = (cart) => {
  const cartStore = useCartStore();
  const { isFreeGiftItem } = cartStore;
  const { resolvedFreeGiftProduct } = storeToRefs(cartStore);

  const promo = resolvedFreeGiftProduct.value;
  if (!promo) return null;
  const giftItems = cart.items.filter(isFreeGiftItem);
  const normalCartItems = cart.items.filter((item) => !isFreeGiftItem(item));
  if (!normalCartItems.length) {
    return {
      add: [],
      update: [],
      remove: giftItems.map((item) => ({ key: item.key, quantity: 0 })),
    };
  }

  const cartSku = promo.cartSku ?? promo.displaySku;
  const giftProduct = getProductBySku(cartSku);
  const bundleDefinition = giftProduct?.bundleDefinition;
  const exception = bundleDefinition?.additionalBundleDetails?.exception;
  const otherCartProductHandles = normalCartItems.map((item) => item.handle);
  const exceptionApplies =
    !!exception &&
    otherCartProductHandles.every((handle) => exception.productHandles.includes(handle));

  const primaryGiftItem = giftItems[0];
  const duplicateGiftItems = giftItems.slice(1);
  const needsReroll =
    !!primaryGiftItem &&
    (giftItemNeedsReroll(primaryGiftItem, cart.items) ||
      giftItemExceptionMismatch(primaryGiftItem, exception, exceptionApplies));

  if (primaryGiftItem && !needsReroll) {
    return {
      add: [],
      update: primaryGiftItem.quantity > 1 ? [{ key: primaryGiftItem.key, quantity: 1 }] : [],
      remove: duplicateGiftItems.map((item) => ({ key: item.key, quantity: 0 })),
    };
  }

  const staleGiftItems = needsReroll ? giftItems : duplicateGiftItems;
  const removeStaleGiftItems = staleGiftItems.map((item) => ({ key: item.key, quantity: 0 }));

  const variantId = giftProduct?.attributes.variantId ?? null;
  if (!variantId) {
    console.warn(`Free gift promo: could not find variantId for SKU ${cartSku}`);
    return null;
  }

  const baseItem: AddToCartItem = {
    id: variantId,
    quantity: 1,
    properties: {
      _free_gift: 'true',
      _free_gift_display_sku: promo.displaySku,
      _max_cart_quantity: '1',
    },
  };

  if (!bundleDefinition) {
    return { add: [baseItem], update: [], remove: removeStaleGiftItems };
  }

  const itemWithBundleDetails: AddToCartItem = {
    ...baseItem,
    properties: {
      ...baseItem.properties,
      _custom_bundle_details: JSON.stringify(buildFixedBundleCustomDetails(bundleDefinition)),
    },
  };
  const baselineItems = cart.items.filter((item) => !staleGiftItems.includes(item));
  const { resolveRandomisationAtcObject } = useRandomisation(
    ref(
      excludeCappedFamilySkus(
        bundleDefinition.additionalBundleDetails?.randomisation,
        baselineItems,
      ),
    ),
    ref(exception),
    ref(otherCartProductHandles),
  );
  const { resolveMysteryAtcObject } = useMysteryProduct(
    ref(bundleDefinition.additionalBundleDetails?.mysteryProduct),
  );
  const [resolvedItem] = resolveMysteryAtcObject(
    resolveRandomisationAtcObject([itemWithBundleDetails]),
  );

  return { add: [resolvedItem], update: [], remove: removeStaleGiftItems };
};
